import React, { useState } from "react";
import { Plus, Trash2, GripVertical, Edit3, BarChart3 } from "lucide-react";
import { ICON_OPTIONS, TYPE_OPTIONS, ICON_MAP } from "./AdminConstants";
import { CollapsibleSection, FormField } from "./AdminShared";

function SidebarConfigTab({ config, setConfig }) {
  const sidebar = config.function_sidebar;
  const [editingItem, setEditingItem] = useState(null);

  const addSection = () => {
    const nextLetter = String.fromCharCode(97 + Object.keys(sidebar).length);
    setConfig((prev) => ({ ...prev, function_sidebar: { ...prev.function_sidebar, [`section_${nextLetter}`]: { name: `section_${nextLetter}`, items: [] } } }));
  };

  const removeSection = (sectionKey) => {
    if (!window.confirm(`Delete "${sectionKey}" and all its items?`)) return;
    setConfig((prev) => { const updated = { ...prev.function_sidebar }; delete updated[sectionKey]; return { ...prev, function_sidebar: updated }; });
  };

  const addItem = (sectionKey) => {
    const newItem = { key: `new_item_${Date.now()}`, type: "report", label: "New Item", icon: "BarChart3", buttonText: "View" };
    setConfig((prev) => ({ ...prev, function_sidebar: { ...prev.function_sidebar, [sectionKey]: { ...prev.function_sidebar[sectionKey], items: [...prev.function_sidebar[sectionKey].items, newItem] } } }));
    setEditingItem(`${sectionKey}-${newItem.key}`);
  };

  const removeItem = (sectionKey, itemKey) => {
    setConfig((prev) => ({ ...prev, function_sidebar: { ...prev.function_sidebar, [sectionKey]: { ...prev.function_sidebar[sectionKey], items: prev.function_sidebar[sectionKey].items.filter((i) => i.key !== itemKey) } } }));
    if (editingItem === `${sectionKey}-${itemKey}`) setEditingItem(null);
  };

  const updateItem = (sectionKey, itemKey, field, value) => {
    setConfig((prev) => ({ ...prev, function_sidebar: { ...prev.function_sidebar, [sectionKey]: { ...prev.function_sidebar[sectionKey], items: prev.function_sidebar[sectionKey].items.map((item) => item.key === itemKey ? { ...item, [field]: value } : item) } } }));
    if (field === "key") setEditingItem(`${sectionKey}-${value}`);
  };

  const updateSectionName = (sectionKey, newName) => {
    setConfig((prev) => ({ ...prev, function_sidebar: { ...prev.function_sidebar, [sectionKey]: { ...prev.function_sidebar[sectionKey], name: newName } } }));
  };

  const moveItem = (sectionKey, index, direction) => {
    setConfig((prev) => {
      const items = [...prev.function_sidebar[sectionKey].items];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= items.length) return prev;
      [items[index], items[newIndex]] = [items[newIndex], items[index]];
      return { ...prev, function_sidebar: { ...prev.function_sidebar, [sectionKey]: { ...prev.function_sidebar[sectionKey], items } } };
    });
  };

  return (
    <div className="admin-tab-content">
      <div className="admin-tab-content__toolbar">
        <button className="admin-btn admin-btn--primary" onClick={addSection}><Plus size={14} /> Add Section</button>
      </div>
      {Object.entries(sidebar).map(([sectionKey, section]) => (
        <CollapsibleSection key={sectionKey} title={`Section: ${section.name}`} subtitle={`${section.items.length} item(s)`}
          actions={<div className="admin-section__action-btns">
            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => addItem(sectionKey)}><Plus size={12} /> Add Item</button>
            <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => removeSection(sectionKey)}><Trash2 size={12} /></button>
          </div>}
        >
          <div className="admin-sidebar-section-meta">
            <FormField label="Section Name">
              <input className="admin-input" value={section.name} onChange={(e) => updateSectionName(sectionKey, e.target.value)} />
            </FormField>
          </div>
          <div className="admin-sidebar-items">
            {section.items.map((item, index) => {
              const isEditing = editingItem === `${sectionKey}-${item.key}`;
              const IconComponent = ICON_MAP[item.icon] || BarChart3;
              return (
                <div className={`admin-sidebar-item ${isEditing ? "admin-sidebar-item--editing" : ""}`} key={`${sectionKey}-${index}`}>
                  <div className="admin-sidebar-item__header">
                    <div className="admin-sidebar-item__left">
                      <div className="admin-sidebar-item__grip"><GripVertical size={14} /></div>
                      <div className="admin-sidebar-item__icon-preview"><IconComponent size={16} /></div>
                      <div className="admin-sidebar-item__info">
                        <span className="admin-sidebar-item__label">{item.label}</span>
                        <span className="admin-sidebar-item__meta">{item.type} · {item.key}</span>
                      </div>
                    </div>
                    <div className="admin-sidebar-item__actions">
                      <button className="admin-btn-icon" onClick={() => moveItem(sectionKey, index, -1)} disabled={index === 0}>↑</button>
                      <button className="admin-btn-icon" onClick={() => moveItem(sectionKey, index, 1)} disabled={index === section.items.length - 1}>↓</button>
                      <button className="admin-btn-icon" onClick={() => setEditingItem(isEditing ? null : `${sectionKey}-${item.key}`)}><Edit3 size={13} /></button>
                      <button className="admin-btn-icon admin-btn-icon--danger" onClick={() => removeItem(sectionKey, item.key)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  {isEditing && (
                    <div className="admin-sidebar-item__edit-form">
                      <div className="admin-form-grid admin-form-grid--3">
                        <FormField label="Key" required><input className="admin-input" value={item.key} onChange={(e) => updateItem(sectionKey, item.key, "key", e.target.value)} /></FormField>
                        <FormField label="Label" required><input className="admin-input" value={item.label} onChange={(e) => updateItem(sectionKey, item.key, "label", e.target.value)} /></FormField>
                        <FormField label="Type"><select className="admin-input admin-select" value={item.type} onChange={(e) => updateItem(sectionKey, item.key, "type", e.target.value)}>{TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}</select></FormField>
                        <FormField label="Icon"><select className="admin-input admin-select" value={item.icon} onChange={(e) => updateItem(sectionKey, item.key, "icon", e.target.value)}>{ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}</select></FormField>
                        <FormField label="Button Text"><input className="admin-input" value={item.buttonText} onChange={(e) => updateItem(sectionKey, item.key, "buttonText", e.target.value)} /></FormField>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {section.items.length === 0 && (
              <div className="admin-empty-state admin-empty-state--sm">
                <p>No items</p>
                <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => addItem(sectionKey)}><Plus size={12} /> Add Item</button>
              </div>
            )}
          </div>
        </CollapsibleSection>
      ))}
    </div>
  );
}

export default SidebarConfigTab;
